import React, { useCallback, useEffect, useState } from 'react'

import { Drawer, Tabs, Result } from 'antd'
import omit from 'lodash/omit'
import { withRouter } from 'react-router-dom'

import CustomReportFilters from '../../../components/forms/CustomReportFilters'
import VideoModal from '../../../components/media/VideoModal'
import PlayButton from '../../../components/touchables/PlayButton'
import useGogeFilters from '../../../hooks/useGogeFilters'
import usePageTitle from '../../../hooks/usePageTitle'
import usePrevious from '../../../hooks/usePrevious'
import useUniversitiesForClient from '../../../hooks/useUniversitiesForClient'
// import sendFullStoryEvent from '../../../utils/analytics/sendFullStoryEvent'
import { TUTORIALS } from '../../../utils/applicationConstants'
// import fullstory from '../../../utils/constants/fullstory'
import {
  removeAssociatesFacetOptionForUniversities,
  removeFilterOptionFromAllFacets,
} from '../../../utils/data/utils'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import ReportHeader from '../components/AuthenticatedLayout/components/AuthenticatedHeader/ReportHeader'

import Filters from './components/Filters'
import { useGraduateOutcomesOverview } from './hooks/useGraduateOutcomesOverview'
import { useUniversityClientUserProfile } from './hooks/useUniversityClientUserProfile'
import EmployerTrendsTab from './tabs/EmployerTrends/'
import Overview from './tabs/Overview/'
import SkillsTab from './tabs/Skills'

const TabPane = /** @type {any} */ (Tabs.TabPane)

const srOnlyStyles = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: /** @type {'absolute'} */ ('absolute'),
  whiteSpace: /** @type {'nowrap'} */ ('nowrap'),
  width: '1px',
}

const tabKeys = {
  employers: 'employers',
  overview: 'overview',
  skills: 'skills',
}

const Outcomes = ({ history, location }) => {
  const [filtersDrawerVisible, setFiltersDrawerVisible] = useState(false)
  const [activeKey, setActiveKey] = useState(tabKeys.overview)
  const [videoModalShown, setVideoModalShown] = useState(false)
  const [topLevelFilters, setTopLevelFilters] = useState({})
  const [liveAnnouncement, setLiveAnnouncement] = useState('')

  let currentPath = location.pathname.substring(location.pathname.lastIndexOf('/') + 1)

  if (!currentPath || currentPath === '' || currentPath === 'graduate-outcomes')
    currentPath = tabKeys.overview

  if (currentPath !== activeKey) setActiveKey(currentPath)

  usePageTitle('Graduate Outcomes')

  const announceLiveMessage = useCallback(message => {
    setLiveAnnouncement('')
    window.requestAnimationFrame(() => {
      setLiveAnnouncement(message)
    })
  }, [])

  const tabLabels = {
    [tabKeys.overview]: 'Overview',
    [tabKeys.employers]: 'Employer Trends',
    [tabKeys.skills]: 'Skills',
  }

  const handleTabChange = key => {
    setLiveAnnouncement(`${tabLabels[key] || key} tab selected`)
    history.replace(`/app/graduate-outcomes/${key === tabKeys.overview ? '' : key}`)
  }

  const {
    clientName,
    multiSchoolDims,
    taxonomy,
    url,
    universityClientUserProfileLoading,
  } = useUniversityClientUserProfile()

  const {
    finalTaxonomy,
    universities: universitiesToSubmit,
    finalUrl,
    grandTotal,
    universityNameDisplay,
  } = useUniversitiesForClient({
    clientName,
    multiSchoolDims,
    taxonomy,
    topLevelFilters,
    url,
  })

  const {
    onChangeCustomFilters,
    checkTopLevelFiltersExists,
    handleClickFilterClick,
    clearClickFilter,
    handleGeoSelection,
    clearIndividualFilter,
    clearTopLevelFilters,
    overviewClickFilters,
    setRecordCount,
  } = useGogeFilters({
    grandTotal,
    onFilterApplied: message => announceLiveMessage(message),
    onFilterRemoved: message => announceLiveMessage(message),
    setTopLevelFilters,
    topLevelFilters,
  })

  const {
    facets,
    overviewData,
    loadingGraduateOutcomesOverviewData,
    overviewRecordCount,
    suppliedTopLevelFilters,
    graduateOutcomesOverviewError,
    clickFilterOptions,
  } = useGraduateOutcomesOverview({
    clientName,
    multiSchoolDims,
    overviewClickFilters,
    taxonomy: finalTaxonomy,
    topLevelFilters,
    universities: universitiesToSubmit,
  })

  // Add this useEffect to disable background scrolling when drawer is open
  useEffect(() => {
    if (filtersDrawerVisible) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to ensure overflow is reset when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [filtersDrawerVisible])

  const handlePlayButtonClick = () => {
    setVideoModalShown(true)
    // const eventProperties = {
    //   location_str: TUTORIALS.graduateOutcomes.title,
    //   video_src_str: TUTORIALS.graduateOutcomes.videoSrc,
    // }
    // sendFullStoryEvent(fullstory.eventNames.videoClick, eventProperties)
  }

  const prevLoadingGraduateOutcomesOverviewData = usePrevious(loadingGraduateOutcomesOverviewData)
  const prevOverviewRecordCount = usePrevious(overviewRecordCount)
  useEffect(() => {
    const loadingCompleted =
      prevLoadingGraduateOutcomesOverviewData === true && !loadingGraduateOutcomesOverviewData
    const countChanged =
      prevOverviewRecordCount !== undefined && prevOverviewRecordCount !== overviewRecordCount

    if (!loadingCompleted && !countChanged) return
    if (overviewRecordCount === 0 && !loadingCompleted) return

    announceLiveMessage(`Results updated. Showing ${overviewRecordCount} alumni.`)
  }, [
    announceLiveMessage,
    loadingGraduateOutcomesOverviewData,
    overviewRecordCount,
    prevLoadingGraduateOutcomesOverviewData,
    prevOverviewRecordCount,
  ])

  return (
    <AuthenticatedLayout>
      {/* biome-ignore lint/a11y/useSemanticElements: <output> has inconsistent screen reader support across browsers */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={srOnlyStyles}
        onBlur={() => setLiveAnnouncement('')}
      >
        {liveAnnouncement}
      </div>
      <VideoModal
        visible={videoModalShown}
        onCancel={() => setVideoModalShown(false)}
        tutorial={TUTORIALS.graduateOutcomes}
      />
      <Drawer
        title="Filters"
        placement="right"
        closable={true}
        destroyOnClose
        headerStyle={{ paddingLeft: '16px' }}
        getContainer={false}
        onClose={() => setFiltersDrawerVisible(false)}
        visible={filtersDrawerVisible}
        style={{ overflow: 'auto' }}
        width={300}
        className="right-facets"
      >
        <CustomReportFilters
          onChange={onChangeCustomFilters}
          pageName="graduate-outcomes"
          onCloseDrawer={() => setFiltersDrawerVisible(false)}
          facets={
            /** @type {any} */ (omit(
              removeAssociatesFacetOptionForUniversities(
                removeFilterOptionFromAllFacets(facets, 'high school'),
                [clientName, ...universitiesToSubmit],
              ),
              '__typename',
            ))
          }
          suppliedFilters={suppliedTopLevelFilters}
        />
      </Drawer>
      <div className="card-container">
        <div className="report-header-container">
          <ReportHeader
            universityNameDisplay={universityNameDisplay}
            title="Graduate Outcomes "
            description="Learn about the career outcomes of your alumni, and use the filters to answer specific questions."
            recordCount={overviewRecordCount}
            totalRecords={grandTotal}
            url={finalUrl}
            videoLink={
              <PlayButton
                handleClick={handlePlayButtonClick}
                additionalStyles={{ marginLeft: '0' }}
              />
            }
          />
          <Filters
            topLevelFilters={topLevelFilters}
            clearIndividualFilter={clearIndividualFilter}
            loadingGraduateOutcomesOverviewData={loadingGraduateOutcomesOverviewData}
            clearTopLevelFilters={clearTopLevelFilters}
            facets={facets}
            checkTopLevelFiltersExists={checkTopLevelFiltersExists}
            setFiltersDrawerVisible={setFiltersDrawerVisible}
          />
        </div>
        <Tabs
          activeKey={activeKey}
          className="report-body"
          onTabClick={key => handleTabChange(key)}
          renderTabBar={(props, DefaultTabBar) => (
            <DefaultTabBar
              {...props}
              renderTabBarNode={node =>
                React.cloneElement(node, {
                  'aria-controls': `tabpanel-go-${node.key}`,
                  id: `tab-go-${node.key}`,
                  onKeyDown: e => {
                    if (e.key === 'Enter') e.currentTarget.click()
                  },
                })
              }
            />
          )}
        >
          <TabPane
            tab={<span data-testid="overview-tab">Overview</span>}
            key="overview"
            id="tabpanel-go-overview"
            aria-labelledby="tab-go-overview"
            forceRender={false}
          >
            {!loadingGraduateOutcomesOverviewData && graduateOutcomesOverviewError ? (
              <Result
                status="500"
                title="Hmm..."
                subTitle={`This is what they call "Technical difficulties"`}
              />
            ) : (
              <div className="card-content">
                <div className="data-card">
                  <Overview
                    handleClickFilterClick={handleClickFilterClick}
                    clearClickFilter={clearClickFilter}
                    overviewData={overviewData}
                    loading={loadingGraduateOutcomesOverviewData}
                    overviewClickFilters={overviewClickFilters}
                    handleGeoSelection={handleGeoSelection}
                    clickFilterOptions={clickFilterOptions}
                    isActiveTab={activeKey === 'overview'}
                  />
                </div>
              </div>
            )}
          </TabPane>
          <TabPane
            tab={<span data-testid="employer-trends-tab">Employer Trends</span>}
            key="employers"
            id="tabpanel-go-employers"
            aria-labelledby="tab-go-employers"
          >
            <div tabIndex={activeKey === 'employers' ? 0 : -1}>
              <EmployerTrendsTab
                universityNameDisplay={universityNameDisplay}
                loadingGraduateOutcomesOverviewData={loadingGraduateOutcomesOverviewData}
                clientName={clientName}
                finalTaxonomy={finalTaxonomy}
                topLevelFilters={topLevelFilters}
                universitiesToSubmit={universitiesToSubmit}
                universityClientUserProfileLoading={universityClientUserProfileLoading}
                isActiveTab={activeKey === 'employers'}
              />
            </div>
          </TabPane>
          <TabPane
            tab={<span data-testid="skills-tab">Skills</span>}
            key="skills"
            id="tabpanel-go-skills"
            aria-labelledby="tab-go-skills"
          >
            <div tabIndex={activeKey === 'skills' ? 0 : -1}>
              <SkillsTab
                topLevelFilters={topLevelFilters}
                finalTaxonomy={finalTaxonomy}
                universitiesToSubmit={universitiesToSubmit}
                clientName={clientName}
                setRecordCount={setRecordCount}
                isActiveTab={activeKey === 'skills'}
              />
            </div>
          </TabPane>
        </Tabs>
      </div>
    </AuthenticatedLayout>
  )
}

export default withRouter(Outcomes)
